<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const visible = ref(false);

function open() {
  visible.value = true;
}
function close() {
  visible.value = false;
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
    close();
  }
}

// keys/labels are resolved at render time so locale switches re-render correctly.
const shortcuts = [
  { keys: '⌘K / Ctrl+K', desc: 'commandPalette.help_palette' },
  { keys: '?', desc: 'commandPalette.help_help' },
  { keys: 'Esc', desc: 'commandPalette.help_close' },
];

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="help-overlay" @click.self="close">
      <div
        class="help-panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('commandPalette.help_title')"
        tabindex="-1"
        @keydown="onKeydown"
      >
        <div class="help-header">
          <span class="help-title">{{ t('commandPalette.help_title') }}</span>
        </div>
        <ul class="help-list">
          <li v-for="s in shortcuts" :key="s.keys" class="help-row">
            <kbd class="help-kbd">{{ s.keys }}</kbd>
            <span class="help-desc">{{ t(s.desc) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.help-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 16vh;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(2px);
}
.help-panel {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  outline: none;
}
.help-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.help-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.help-list {
  list-style: none;
  margin: 0;
  padding: 12px 20px 20px;
}
.help-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}
.help-kbd {
  flex-shrink: 0;
  min-width: 96px;
  display: inline-flex;
  justify-content: center;
  padding: 4px 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: #334155;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.help-desc {
  font-size: 14px;
  color: #475569;
}
</style>
